/* Header behaviors: mobile drawer, search bar, dropdowns, sticky header,
   localization auto-submit. */

(function () {
  'use strict';

  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      this.menuToggle = this.querySelector('.header__menu-toggle');
      this.drawer = this.querySelector('.menu-drawer');
      this.overlay = this.querySelector('.menu-drawer__overlay');
      this.drawerClose = this.querySelector('.menu-drawer__close');
      this.searchToggles = this.querySelectorAll('.header__search-toggle');
      this.searchBar = this.querySelector('.header__search');
      this.searchClose = this.querySelector('.header__search-close');

      this.bindDrawer();
      this.bindSearch();
      this.bindSticky();

      this.onKeydown = (event) => {
        if (event.key === 'Escape') {
          this.closeDrawer();
          this.closeSearch();
        }
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
    }

    /* Mobile drawer ------------------------------------------------ */

    bindDrawer() {
      if (!this.menuToggle || !this.drawer) return;

      this.menuToggle.addEventListener('click', () => {
        this.drawer.hidden ? this.openDrawer() : this.closeDrawer();
      });
      this.drawerClose?.addEventListener('click', () => this.closeDrawer());
      this.overlay?.addEventListener('click', () => this.closeDrawer());

      // Basic focus trap
      this.drawer.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const focusable = this.drawer.querySelectorAll(
          'a[href], button:not([disabled]), select, summary'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    openDrawer() {
      this.drawer.hidden = false;
      this.overlay.hidden = false;
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        this.overlay.classList.add('is-visible');
      });
      this.menuToggle.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.drawerClose?.focus();
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.classList.remove('is-open');
      this.overlay.classList.remove('is-visible');
      this.menuToggle.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      window.setTimeout(() => {
        this.drawer.hidden = true;
        this.overlay.hidden = true;
      }, 300);
      this.menuToggle.focus();
    }

    /* Search bar --------------------------------------------------- */

    bindSearch() {
      if (!this.searchBar) return;
      this.searchToggles.forEach((toggle) => {
        toggle.addEventListener('click', () => {
          this.searchBar.hidden ? this.openSearch(toggle) : this.closeSearch();
        });
      });
      this.searchClose?.addEventListener('click', () => this.closeSearch());
    }

    openSearch(toggle) {
      this.searchBar.hidden = false;
      this.searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      this.lastSearchToggle = toggle;
      this.searchBar.querySelector('input[type="search"]')?.focus();
    }

    closeSearch() {
      if (!this.searchBar || this.searchBar.hidden) return;
      this.searchBar.hidden = true;
      this.searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.lastSearchToggle?.focus();
    }

    /* Sticky header ------------------------------------------------ */

    bindSticky() {
      const type = this.dataset.stickyType;
      this.lastScroll = 0;

      window.addEventListener(
        'scroll',
        () => {
          const y = window.scrollY;
          this.classList.toggle('is-scrolled', y > 40);

          if (type === 'on-scroll-up') {
            if (y > this.lastScroll && y > 200) {
              this.classList.add('header-wrapper--hidden');
            } else {
              this.classList.remove('header-wrapper--hidden');
            }
          }
          this.lastScroll = y;
        },
        { passive: true }
      );
    }
  }

  if (!customElements.get('header-component')) {
    customElements.define('header-component', HeaderComponent);
  }

  /* Desktop dropdown disclosure ------------------------------------ */

  class DetailsDisclosure extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      this.details.addEventListener('toggle', () => {
        this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');
      });

      // Open on hover for pointer devices
      this.addEventListener('mouseenter', () => {
        if (window.matchMedia('(hover: hover)').matches) this.details.open = true;
      });
      this.addEventListener('mouseleave', () => {
        if (window.matchMedia('(hover: hover)').matches) this.details.open = false;
      });

      // Close when focus leaves or on outside click
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.details.open = false;
      });
      document.addEventListener('click', (event) => {
        if (this.details.open && !this.contains(event.target)) this.details.open = false;
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          this.details.open = false;
          this.summary.focus();
        }
      });
    }
  }

  if (!customElements.get('details-disclosure')) {
    customElements.define('details-disclosure', DetailsDisclosure);
  }

  /* Localization selector: submit on change ------------------------ */

  class LocalizationSelector extends HTMLElement {
    connectedCallback() {
      const select = this.querySelector('select');
      select?.addEventListener('change', () => {
        select.form?.submit();
      });
    }
  }

  if (!customElements.get('localization-selector')) {
    customElements.define('localization-selector', LocalizationSelector);
  }

  /* Announcement bar rotation -------------------------------------- */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(this.querySelectorAll('.announcement-bar__message'));
      if (this.messages.length < 2) return;

      this.index = 0;
      this.querySelector('[name="previous"]')?.addEventListener('click', () => this.show(this.index - 1));
      this.querySelector('[name="next"]')?.addEventListener('click', () => this.show(this.index + 1));

      if (this.dataset.autorotate === 'true') {
        const speed = parseInt(this.dataset.speed, 10) || 5000;
        this.timer = window.setInterval(() => this.show(this.index + 1), speed);
        this.addEventListener('mouseenter', () => window.clearInterval(this.timer));
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    show(nextIndex) {
      this.messages[this.index].hidden = true;
      this.messages[this.index].classList.remove('is-active');
      this.index = (nextIndex + this.messages.length) % this.messages.length;
      this.messages[this.index].hidden = false;
      this.messages[this.index].classList.add('is-active');
    }
  }

  if (!customElements.get('announcement-bar')) {
    customElements.define('announcement-bar', AnnouncementBar);
  }
})();
